import { usePostHog } from "@posthog/react";
import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../allauth/auth/AuthContext";
import { useAppContext } from "../context/AppContext";
import reportWebVitals from "../reportWebVitals";

/**
 * Single PostHog side-effect component: identify, manual pageview (capture_pageview: false), and web vitals.
 * Mount once inside PostHogProvider, AuthContextProvider, and Router (e.g. in Layout).
 * Uses one usePostHog() for efficiency and only runs when the client is available.
 */
export default function PostHogAnalytics() {
  const posthog = usePostHog();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const auth = authContext?.auth;
  const isAuthenticated = auth?.meta?.is_authenticated === true;
  const user = auth?.data?.user;
  const { membership } = useAppContext();
  const loadTimingsSent = useRef(false);

  // Load timings (DOM ready, React ready, Auth bootstrap) — send once per page load
  useEffect(() => {
    if (!posthog || loadTimingsSent.current || typeof window === "undefined") return;
    const t = window.__loadTimings;
    if (!t) return;
    const domReady = t.dom_ready_ms;
    const reactReady = t.react_ready_ms;
    const authBootstrap = t.auth_bootstrap_ms;
    if (domReady != null || reactReady != null || authBootstrap != null) {
      loadTimingsSent.current = true;
      posthog.capture("app_load_timings", {
        dom_ready_ms: domReady,
        react_ready_ms: reactReady,
        auth_bootstrap_ms: authBootstrap,
        time_origin: typeof performance !== "undefined" ? performance.timeOrigin : undefined
      });
    }
  }, [posthog]);

  // Identify on login, reset on logout
  useEffect(() => {
    if (!posthog) return;
    if (isAuthenticated) {
      posthog.identify(String(user?.id), {
        ...user
      });
    } else if (!isAuthenticated) {
      posthog.reset();
    }
  }, [posthog, isAuthenticated, user]);

  // Register user roles as super properties so every event includes them
  const roles = membership?.roles;
  useEffect(() => {
    if (!posthog) return;
    if (roles && roles.length > 0) {
      posthog.register({ user_roles: roles });
    } else {
      posthog.unregister('user_roles');
    }
  }, [posthog, roles]);

  // Manual pageview (used when PostHogProvider has capture_pageview: false)
  useEffect(() => {
    if (!posthog) return;
    let url = window.origin + location.pathname;
    if (location.search) url += location.search;
    if (location.hash && location.hash !== "#") url += location.hash;
    posthog.capture("$pageview", { $current_url: url });
  }, [posthog, location.pathname, location.search, location.hash]);

  // Web vitals
  useEffect(() => {
    if (!posthog) return;
    reportWebVitals((metric) => {
      posthog.capture("web_vitals", {
        name: metric.name,
        value: metric.value,
        delta: metric.delta,
        id: metric.id
      });
    });
  }, [posthog]);

  return null;
}
